import { useCallback, useState } from "react"
import { CConfirmModal } from "@shared/ui/CConfirmModal"
import { addLogError } from "@/model/logger"

type ConfirmCallback = () => Promise<unknown> | void

interface ConfirmationOptions {
  title?: string
  confirmText?: string
  cancelText?: string
  confirmColor?: "blue" | "red" | "gray" | "light"
}

interface ConfirmationState {
  isOpen: boolean
  message: string
  callback: ConfirmCallback | null
  isProcessing: boolean
  options: ConfirmationOptions
}

/**
 * Хук для локального управления диалогами подтверждения.
 */
export const useLocalConfirmation = () => {
  const [confirmationState, setConfirmationState] = useState<ConfirmationState>({
    isOpen: false,
    message: "",
    callback: null,
    isProcessing: false,
    options: {},
  })

  /**
   * Открывает диалог подтверждения
   */
  const showConfirmation = useCallback(
    (message: string | undefined, callback: ConfirmCallback, options?: ConfirmationOptions) => {
      setConfirmationState({
        isOpen: true,
        message: message || "",
        callback,
        isProcessing: false,
        options: options || {},
      })
    },
    [],
  )

  /**
   * Обработчик нажатия кнопки "Отмена"
   */
  const handleCancel = useCallback((e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation()
    }

    setConfirmationState({
      isOpen: false,
      message: "",
      callback: null,
      isProcessing: false,
      options: {},
    })
  }, [])

  /**
   * Обработчик подтверждения действия
   */
  const { callback } = confirmationState
  const handleConfirm = useCallback(
    async (e?: React.MouseEvent) => {
      if (e) {
        e.stopPropagation()
      }

      if (callback) {
        try {
          setConfirmationState((prev) => ({ ...prev, isProcessing: true }))

          const result = callback()

          if (result instanceof Promise) {
            await result
          }

          setConfirmationState({
            isOpen: false,
            message: "",
            callback: null,
            isProcessing: false,
            options: {},
          })
        } catch (error) {
          addLogError("Error during confirmation action:", error)
          setConfirmationState((prev) => ({ ...prev, isProcessing: false }))
        }
      }
    },
    [callback],
  )

  /**
   * Компонент модального окна для встраивания в конец компонента
   */
  const ConfirmationModal = useCallback(() => {
    return (
      <CConfirmModal
        isShow={confirmationState.isOpen}
        onClose={handleCancel}
        onSuccess={handleConfirm}
        isProcessing={confirmationState.isProcessing}
        description={confirmationState.message}
        title={confirmationState.options.title}
        confirmText={confirmationState.options.confirmText}
        cancelText={confirmationState.options.cancelText}
        confirmColor={confirmationState.options.confirmColor}
      />
    )
  }, [confirmationState, handleCancel, handleConfirm])

  return {
    showConfirmation,
    handleCancel,
    ConfirmationModal,
  }
}
